/**
 * 중점 분석 «영역» 지정 모달 — 두 진입이 **같은 모달**을 쓴다.
 *
 * ① 업로드 미리보기(`PhotoUploadSheet`): 올리기 전에 지정 → 첫 분석부터 적용된다(낭비 0).
 * ② 사진 상세(점검 상세의 「다시 분석」 옆): 결과를 보고 범위를 좁혀 다시 돌린다.
 *
 * 둘이 같은 컴포넌트를 쓰므로 `object-contain` 여백 계산·좌표 변환 사본이 늘지 않는다
 * (수학은 `region-geometry.ts`, 표시 영역 계산은 `AnalysisRegionEditor` 한 곳).
 *
 * ⚠️ **`confirmLabel`·`warning`으로 두 진입의 「확인이 무엇을 뜻하나」를 가른다.** ①은 영역을
 * 로컬 상태에 담아 두는 것이라 되돌릴 수 있지만, ②는 **재분석이 즉시 돌면서 그 사진의 발견사항을
 * 전량 삭제하고 새로 만든다**(서버 `photo-analysis.processor.ts` 「재분석: 기존 이력 삭제」) —
 * 사용자가 손댄 편집·개선대책 선택도 사라진다. 그 경고를 ②에만 띄우는 것이 이 prop의 목적이다.
 *
 * 경고를 여기 붙인 근거: 기존 단건 「다시 분석」은 desktop `DropdownMenu`·fold·phone **셋 다 확인
 * 없이 즉시** 실행하고, 벌크 쪽 `BulkActionConfirmDialog` 문구도 삭제를 말하지 않는다(2026-10-05
 * 확인). 영역 기능은 「결과를 보고 좁혀 다시 돌린다」는 흐름을 새로 만들어 재분석 빈도를 올리므로
 * 결정 기록대로 **영역 재분석 쪽에 먼저** 붙였다 — 기존 두 경로에도 붙일지는 별건이다.
 *
 * ## ⚠️ 이 컴포넌트를 렌더하는 RTL spec은 레포에 **0건**이다 (2026-10-06 확인)
 *
 * 이 파일을 읽는 검사는 **소스 스캔 가드 하나**뿐이고(`__tests__/regionCopyDoesNotClaimCropping`)
 * 그것이 재는 것은 **문구**다. 그러니 **`role="dialog"`·Esc·포커스 트랩·오버레이가 닫지 않는 것·
 * z 층(9997/9999)은 그 가드의 초록과 아무 상관이 없다 — dev 실측으로만 검증된다.**
 * 「가드가 초록이니 닫힘 동작도 지켜진다」로 읽지 말 것.
 *
 * 실측 경로: ② 점검 상세 → 사진 「범위 지정 후 다시 분석」 → 사각형을 그린 뒤 **어두운 바깥을
 * 클릭** → **닫히지 않아야** 한다(그린 영역이 버려지지 않는다) · ① 업로드 시트 진입에서는 이
 * 모달이 **시트(9997) 위에** 떠야 한다.
 */
import { useEffect, useRef, useState } from 'react';
import type { AnalysisRegion } from '@safetyhub/shared';
import { Button, Modal } from '@safetyhub/ui';
import { AnalysisRegionEditor } from './AnalysisRegionEditor';

export interface AnalysisRegionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 사진 URL (업로드 전이면 `URL.createObjectURL` 결과) */
  imageUrl: string;
  /** 저장된 영역 — 모달을 열 때의 시작값. `null`이면 전체 분석 */
  initialRegion: AnalysisRegion | null;
  /**
   * 확인 — `null`은 **해제**(다음 분석은 전체)이고 객체는 교체다.
   * 두 상태를 그대로 넘기는 것이 중요하다: 호출부가 `null`을 「변경 없음」으로 바꿔 읽으면
   * 사용자가 누른 「영역 해제」가 아무 일도 안 하게 된다.
   */
  onConfirm: (region: AnalysisRegion | null) => void;
  /** 확인 버튼 문구 — 진입에 따라 「적용」/「영역으로 다시 분석」 */
  confirmLabel?: string;
  /** 확인이 되돌릴 수 없는 일을 할 때의 경고(사진 상세 재분석 진입) */
  warning?: string;
  isSubmitting?: boolean;
}

export function AnalysisRegionModal({
  isOpen,
  onClose,
  imageUrl,
  initialRegion,
  onConfirm,
  confirmLabel = '적용',
  warning,
  isSubmitting = false,
}: AnalysisRegionModalProps) {
  const [region, setRegion] = useState<AnalysisRegion | null>(initialRegion);
  const wasOpenRef = useRef(false);

  /**
   * **열리는 순간에만** 저장된 값에서 다시 시작한다 — 닫고 다시 열었을 때 직전 편집이 남아 있으면
   * 「취소했는데 적용된 것처럼」 보인다.
   *
   * ⚠️ `isOpen`뿐 아니라 `initialRegion`이 바뀔 때마다 리셋하면 **그리는 중에 편집이 날아간다.**
   * 사진 상세의 `initialRegion`은 점검 쿼리에서 오고 그 쿼리는 **분석 진행 중 5초마다 폴링**하므로,
   * refetch가 새 객체를 주는 순간(값이 같아도 identity가 다르다) 사용자가 끌고 있던 사각형이
   * 초기값으로 돌아간다. 그래서 **열림 전이**만 본다(`initialRegion`은 그 시점의 값을 읽을 뿐이다).
   */
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) setRegion(initialRegion);
    wasOpenRef.current = isOpen;
  }, [isOpen, initialRegion]);

  if (!isOpen) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title="분석 범위 지정"
      size="xl"
      // ⚠️ **오버레이 클릭으로 닫지 않는다.** 이 모달의 확인은 사용자가 끌어 그린 사각형을
      // 넘기는 것이라, 오버레이 클릭이 닫으면 그 작업이 **확인 없이 버려진다**. 위 `useEffect`가
      // 「취소했는데 적용된 것처럼 보이지 않게」까지 공들여 둔 자리라 더 어긋났다 — 취소 의미는
      // 설계돼 있었고 **실수로 닫히는 경로만** 비어 있었다. 남은 닫기 경로(Esc·✕·취소)는 모두
      // 명시적 행위다.
      closeOnOverlay={false}
      // ⚠️ **`isDirty`를 쓰지 않는다 — 이 z 세계에서는 쓸 수 없다.** base의 dirty 확인 모달 층은
      // `DIRTY_CONFIRM_Z_ABOVE`에서 파생되고 그 표는 `z-[70]~z-[100]`만 담는다. 아래처럼
      // `z-[9999]`를 넘기면 미등재라 **`z-[100]`으로 폴백**해 확인 모달이 이 모달(9999)과 업로드
      // 시트(9997) **아래로 숨는다** — 「보이지 않는 확인」은 조용히 버리는 것보다 나쁘다.
      // 그래서 보호는 위 `closeOnOverlay={false}`가 담당한다(팀장 승인 2026-10-06).
      //
      // ⚠️ **되짚을 조건**: 이 서브트리의 z 세계가 `MODAL_LAYER`(z-[70]~z-[100])로 통합되면 그때는
      // `isDirty`를 쓸 수 있다 — 그 시점에 **위 `closeOnOverlay={false}`를 재검토**할 것(dirty 확인이
      // 보이게 되면 오버레이 클릭을 되살려도 작업이 버려지지 않는다).
      // 이 줄이 없으면 다음 사람이 이 선택을 「base 모달의 dirty 축을 안 쓴 실수」로 읽는다.
      //
      // ⚠️ 이 서브트리는 `MODAL_LAYER`(z-[70]~z-[100])와 **다른 z 세계**다 — 업로드 시트가
      // 9997이고 사진 뷰어가 9998이라, 정책 상수로 낮추면 시트 **아래로 숨는다**. 형제들
      // (`ConfirmDialog`·`PreviousFindingDetailModal`)도 9999를 쓴다.
      overlayZClassName="z-[9999]"
      // 종전 raw 오버레이가 `bg-black/60`이었다 — base 기본(`bg-black/40`)보다 진한 것은 이 모달이
      // 페이지 크롬이 아니라 **업로드 시트/사진 상세 위**에 뜨기 때문이다(그 아래가 이미 밝다).
      overlayBgClassName="bg-black/60"
      // 제출 중에는 닫기를 전면 차단한다 — 버튼 `disabled`만으로는 Esc·✕가 열려 있다.
      closeLocked={isSubmitting}
      // ⚠️ **스크롤을 본문에 건다 — 이 자리에서는 그것이 결함 수정이다.** 종전 값은
      // `panelClassName="max-h-[90vh] overflow-y-auto"`로 **패널**을 스크롤시켜 헤더 ✕와 푸터
      // 취소가 함께 밀렸다. 위 `closeOnOverlay={false}`가 **조건 없이 상시**라(그 이유는 그
      // 주석에 있다 — 지우지 말 것) 닫기 수단이 Esc·✕·푸터 취소 셋뿐이고 **터치에는 Esc가
      // 없어 둘**인데, 그 둘이 전부 스크롤 영역 안이었다. `bodyScroll`은 헤더·푸터를 고정하고
      // 본문만 스크롤해 그 둘을 **항상 보이는 탈출구**로 되돌린다.
      // ⚠️ 심각도는 「닫을 길이 없다」가 아니라 **「항상 보이는 탈출구가 없다」**다(스크롤해서
      // 찾으면 도달은 된다 — base `bodyScroll` JSDoc의 그 정정).
      //
      // ⚠️ **클리핑 컨텍스트가 패널 → 본문으로 한 겹 안쪽으로 옮겨진다**(`overflow-y-auto`가
      // 새 클리핑 컨텍스트를 만든다) — 그래서 치환 전에 「본문 안 절대배치 자식이 본문 경계를
      // 넘을 수 있나」를 셌다: **넷이고 전부 무관하다.** 그 넷(`AnalysisRegionEditor`의 이미지
      // `inset-0` · 드래그 사각형 · svg `inset-0` · 힌트 `inset-x-0 bottom-0`)은 컨테이닝 블록이
      // 그 에디터 루트(`relative … overflow-hidden`)이고 **그 루트가 이미 자기 클리핑 컨텍스트를
      // 가져** 본문 경계에 닿지도 못한다. 모달 본문 자신에는 절대배치·포탈이 0건이다.
      // ⚠️ 이 축은 **tsc·spec·프로브 어느 것도 못 본다**(jsdom에 레이아웃이 없다) — 판정 근거는
      // 코드 독해뿐이니, 에디터 루트에서 `overflow-hidden`을 떼거나 본문에 팝오버를 새로 넣게
      // 되면 **그때 이 판정을 다시 하라**(푸터 쪽으로 넘치는 자식이 새로 잘린다).
      bodyScroll
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {/* 해제는 영역이 있을 때만 — 없을 때 눌러도 아무 일이 없는 버튼을 두면 사용자가
              「해제가 안 된다」로 읽는다(존재가 상태를 뜻하는 자리라 조건부 렌더). */}
          {region !== null ? (
            <button
              type="button"
              onClick={() => setRegion(null)}
              disabled={isSubmitting}
              className="px-3 py-2 text-sm font-medium text-ink-700 bg-white border border-line rounded-card-sm hover:bg-surface-hover disabled:opacity-50"
            >
              범위 해제 (전체 분석)
            </button>
          ) : (
            <span className="text-xs text-ink-400">범위 없음 — 사진 전체를 분석합니다</span>
          )}

          <div className="flex gap-2">
            {/* ⚠️ 취소가 `onClose`를 직접 부른다 — `isDirty`가 없으므로 base의 `requestClose`와
                동작이 같다(차이는 dirty 확인뿐이고 그것을 위에서 쓰지 않는다). `useModalRequestClose`
                는 **children 하위 컴포넌트**에서만 잡히므로(여기서 부르면 null) 지금 쓸 수 없다.
                ⚠️ 나중에 `isDirty`를 켜게 되면 **이 버튼이 그 확인을 우회**한다 — 그때는 footer를
                작은 컴포넌트로 분리해 `useModalRequestClose`를 쓸 것. */}
            <Button variant="secondary" size="md" onClick={onClose} disabled={isSubmitting}>
              취소
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => onConfirm(region)}
              disabled={isSubmitting}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      }
    >
      {/* ⚠️ 「지정한 범위«만» 분석한다」로 쓰지 말 것 — 사진은 통째로 보내고 그 범위에 빨간
          사각형을 그려 **중점**으로 보게 한다(2026-10-06 마커 전환). 그리고 범위 안에
          위험요인이 없으면 **전체 분석 결과로 조용히 돌아가므로**, 사용자에게 보이는 증상은
          「범위가 그냥 안 먹었다」다 — 그래서 둘째 줄이 실제 처방이다(실측 근거는 결정 기록
          2026-10-06-analysis-region-switches-from-cropping-to-markers).
          이 축의 탐지기는 `__tests__/regionCopyDoesNotClaimCropping.test.ts`다 — 두 문단 중
          하나를 지우면 그 **[대조군]**이 red가 된다(되돌림 프로브로 확인). */}
      <p className="text-xs text-ink-500">
        표시한 범위를 AI가 <strong className="font-semibold">중점</strong>으로 봅니다. 한 사진에
        위험요인이 여러 개일 때 그중 하나를 고르는 데 쓰세요.
      </p>
      <p className="text-xs text-ink-500 mt-0.5">
        보고 싶은 위험요인이 범위 <strong className="font-semibold">안에</strong> 들어가게
        그려주세요. 범위 안에 위험요인이 없으면 사진 전체 분석 결과가 나옵니다.
      </p>

      {/* 그리기 표면 — 사진 비율과 무관하게 일정 높이를 주고 object-contain으로 맞춘다 */}
      <div className="mt-3 w-full h-[52vh] min-h-[240px]">
        <AnalysisRegionEditor imageUrl={imageUrl} value={region} onChange={setRegion} />
      </div>

      {warning && (
        <div className="mt-3 px-3 py-2 rounded-card-sm bg-amber-50 border border-amber-200">
          <p className="text-xs text-amber-900">{warning}</p>
        </div>
      )}
    </Modal>
  );
}

export default AnalysisRegionModal;
